/**
 * Phone formatting helpers — mirror of backend/services/customers.py logic.
 *
 * Used by NewTicketDialog to:
 *  - Show per-country digit hints
 *  - Enforce maxLength on the phone input
 *  - Normalize to E.164 for client-side dedupe display
 */

// Local fallback if /api/country-codes hasn't loaded yet.
export const DEFAULT_DIGITS = 8;

export function expectedDigits(countryCode, countryCodes = []) {
  const row = countryCodes.find((c) => c.code === countryCode);
  if (row && row.phone_digits) return row.phone_digits;
  return DEFAULT_DIGITS;
}

/**
 * Admin-configured required national length for a code, or null when the code
 * has no configured length (validation is skipped for those).
 */
export function requiredDigits(countryCode, countryCodes = []) {
  const row = countryCodes.find((c) => c.code === countryCode);
  const n = row?.phone_digits;
  return n ? Number(n) : null;
}

export function digitsOnly(raw) {
  return (raw || "").replace(/\D+/g, "");
}

/**
 * Return a human error string if `raw` does not have exactly the configured
 * number of national digits, else null. Accepts both national-only and full
 * international input. Empty input / unconfigured codes return null.
 */
export function phoneLengthError(countryCode, raw, countryCodes = [], label = "Contact number") {
  const expected = requiredDigits(countryCode, countryCodes);
  if (!expected) return null;
  let digits = digitsOnly(raw);
  if (!digits) return null;
  if (digits.startsWith("00")) digits = digits.slice(2);
  const cc = digitsOnly(countryCode);
  const candidates = new Set([digits.length]);
  if (cc && digits.startsWith(cc)) candidates.add(digits.length - cc.length);
  if (candidates.has(expected)) return null;
  return `${label} must be exactly ${expected} digits`;
}

/** Normalize to E.164 (returns string with leading '+' or empty string). */
export function normalizeE164(countryCode, raw) {
  if (!raw) return "";
  let digits = digitsOnly(raw);
  if (!digits) return "";
  if (digits.startsWith("00")) digits = digits.slice(2);
  const cc = digitsOnly(countryCode);
  if (cc && digits.startsWith(cc)) return `+${digits}`;
  if (cc) return `+${cc}${digits}`;
  return `+${digits}`;
}

export function countryNameForCode(countryCode, countryCodes = []) {
  const row = countryCodes.find((c) => c.code === countryCode);
  return row?.country || "";
}

export function phoneHintLabel(countryCode, countryCodes = []) {
  const digits = requiredDigits(countryCode, countryCodes);
  const country = countryNameForCode(countryCode, countryCodes);
  if (!digits) return country ? `Enter ${country} number` : "Phone number";
  return country
    ? `Enter ${country} number (${digits} digits)`
    : `${digits} digits`;
}
